import * as React from "react";

import TextField from "@mui/material/TextField";
import throttle from "lodash/throttle";
import { ILookupItemModel, ILookupItemSimpleModel } from "../../types/types";
import {
  searchClients,
  searchContacts,
} from "../../store/modules/common/commonActions";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";

export interface IProps {
  onChange?: (
    event: React.ChangeEvent<{}>,
    value: ILookupItemSimpleModel[],
  ) => void;
  helperText: any;
  error: any;
  limit?: number;
  id: string;
  label: string;
  selected: ILookupItemSimpleModel[];
  placeholder?: string;
}
export default function ClientAutoComplete(props: IProps) {
  // const [values, setValues] = React.useState<ILookupItemModel[]>([]);
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState<
    readonly ILookupItemSimpleModel[]
  >([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const { onChange, helperText, error, id, label, selected, placeholder } = props;

  const fetch = React.useMemo(
    () =>
      throttle(async (value: string) => {
        setIsLoading(true);
        try {
          const response = await searchContacts(value);
          setOptions(response ?? []);
        } finally {
          setIsLoading(false);
        }
      }, 300),
    [],
  );

  const getOptionsWithAddNew = React.useMemo(() => {
    if (!inputValue.trim() || inputValue.length < 2) {
      return options;
    }

    const searchTerm = inputValue.trim().toLowerCase();
    const hasExactMatch = options.some(
      (option) => option.name?.toLowerCase() === searchTerm,
    );

    if (!hasExactMatch && inputValue.trim().length > 0) {
      // Add "Add [name]" option at the beginning
      const addNewOption: ILookupItemSimpleModel & { isAddNew?: boolean } = {
        id: -1,
        name: `Add "${inputValue.trim()}"`,
        isAddNew: true,
      };
      return [addNewOption, ...options];
    }

    return options;
  }, [options, inputValue]);

  React.useEffect(() => {
    //trigger search after 1/2 second when user stops typing
    if (inputValue.length >= 2 && inputValue.length <= 10) {
      const search = setTimeout(() => {
        fetch(inputValue);
      }, 500);

      return () => clearTimeout(search);
    }
  }, [inputValue]);

  return (
    <Autocomplete
      multiple
      freeSolo
      id={id}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : `${option.name}`
      }
      filterOptions={(x) => x}
      options={getOptionsWithAddNew}
      autoComplete
      includeInputInList
      fullWidth={true}
      filterSelectedOptions
      value={selected}
      getOptionDisabled={(option) =>
        props.limit && selected.length >= props.limit ? true : false
      }
      getOptionKey={(option) => {
        if (typeof option === "string") {
          return option;
        }
        if (option.id) {
          return option.id;
        }
        return option.id!;
      }}
      // onChange={(event: any, newValue: (ILookupItemSimpleModel | string)[]) => {
      //   const processedValues = newValue.map((item) =>
      //     typeof item === "string"
      //       ? { id: Math.floor(Math.random() * -1e12), name: item }
      //       : item
      //   );
      //   // setValues(processedValues);
      //   onChange!(event, processedValues);
      // }}
      onChange={(event: any, newValue: (ILookupItemSimpleModel | string)[]) => {
        const processedValues = newValue.map((item) => {
          if (typeof item === "string") {
            return { id: Math.floor(Math.random() * -1e12), name: item };
          } else if ((item as any).isAddNew) {
            // Handle "Add new" option - extract the actual name from "Add [name]"
            const actualName = inputValue.trim();
            setInputValue("");
            return {
              id: Math.floor(Math.random() * -1e12),
              name: actualName,
            };
          } else {
            return item;
          }
        });
        onChange!(event, processedValues);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          variant="outlined"
          {...params}
          placeholder={placeholder ?? "Search or add new"}
          label={label}
          fullWidth
          helperText={helperText}
          error={error}
          InputLabelProps={{
            shrink: true,
            sx: {
              // position: "relative",
              // transform: "none",
              // marginBottom: "8px",
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading && <CircularProgress color="primary" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const isAddNew = (option as any).isAddNew;
        return (
          <li
            {...props}
            style={{
              ...props.style,
              fontWeight: isAddNew ? "bold" : "normal",
              color: isAddNew ? "#1976d2" : "inherit",
              fontStyle: isAddNew ? "italic" : "normal",
            }}
          >
            {typeof option === "string" ? option : option.name}
          </li>
        );
      }}
    />
  );
}
